import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import ApplicationModal from './ApplicationModal';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  const handleApplyClick = () => {
    setIsApplicationModalOpen(true);
    setIsOpen(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left spacer for mobile */}
            <div className="w-8 md:hidden"></div>

            {/* Centered Logo */}
            <div className="flex items-center justify-center flex-1 md:flex-none">
              <a 
                href="https://www.kiloterra.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
              >
                {/* Logo with conditional styling */}
                <img 
                  src="/kiloterra_logo_simple_white.png" 
                  alt="KILOTERRA Logo" 
                  className={`h-8 w-auto transition-all duration-300 ${
                    isScrolled ? 'brightness-0 opacity-100' : 'opacity-90'
                  }`}
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('why-join')}
                className={`text-sm font-medium hover:text-kiloterra-yellow transition-colors uppercase ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Why Join
              </button>
              <button
                onClick={() => scrollToSection('getting-started')}
                className={`text-sm font-medium hover:text-kiloterra-yellow transition-colors uppercase ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                The Experience
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className={`text-sm font-medium hover:text-kiloterra-yellow transition-colors uppercase ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                About Us
              </button>
              <button
                onClick={() => scrollToSection('requirements')}
                className={`text-sm font-medium hover:text-kiloterra-yellow transition-colors uppercase ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Requirements
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className={`text-sm font-medium hover:text-kiloterra-yellow transition-colors uppercase ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Testimonials
              </button>
              <button
                onClick={() => scrollToSection('final-cta')}
                className={`text-sm font-medium hover:text-kiloterra-yellow transition-colors uppercase ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Opportunity
              </button>
              <button
                onClick={handleApplyClick}
                className="bg-kiloterra-yellow text-kiloterra-dark px-6 py-2 font-bold hover:bg-opacity-90 transition-all transform hover:scale-105 uppercase tracking-wide"
              >
                Apply Now
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`md:hidden ${isScrolled ? 'text-kiloterra-dark' : 'text-white'}`}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {/* Mobile Navigation */}
            {isOpen && (
              <div className="absolute top-16 left-0 right-0 bg-white shadow-lg md:hidden">
                <div className="px-2 pt-2 pb-3 space-y-1">
                  <button
                    onClick={() => scrollToSection('why-join')}
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-kiloterra-dark w-full text-left uppercase"
                  >
                    Why Join
                  </button>
                  <button
                    onClick={() => scrollToSection('getting-started')}
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-kiloterra-dark w-full text-left uppercase"
                  >
                    The Experience
                  </button>
                  <button
                    onClick={() => scrollToSection('about')}
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-kiloterra-dark w-full text-left uppercase"
                  >
                    About Us
                  </button>
                  <button
                    onClick={() => scrollToSection('requirements')}
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-kiloterra-dark w-full text-left uppercase"
                  >
                    Requirements
                  </button>
                  <button
                    onClick={() => scrollToSection('testimonials')}
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-kiloterra-dark w-full text-left uppercase"
                  >
                    Testimonials
                  </button>
                  <button
                    onClick={() => scrollToSection('final-cta')}
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-kiloterra-dark w-full text-left uppercase"
                  >
                    Opportunity
                  </button>
                  <button
                    onClick={handleApplyClick}
                    className="block mx-3 my-2 bg-kiloterra-yellow text-kiloterra-dark px-6 py-2 font-bold text-center uppercase tracking-wide"
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      <ApplicationModal 
        isOpen={isApplicationModalOpen} 
        onClose={() => setIsApplicationModalOpen(false)}
        source="navigation"
      />
    </>
  );
};

export default Navigation;